// cspell:ignore tnum Segoe nums
const { body } = document;
const containerStyles = [
  "position:absolute",
  "width:auto",
  "font-size:128px",
  "left:-99999px",
].join(" !important;");

/**
 * Returns true if the font used by the browser can be presumed to have a tnum
 * variant for glyphs, and also the browser supports font-variant-numeric.
 * Modified from https://www.samclarke.com/javascript-is-font-available/ under
 * the MIT license https://opensource.org/licenses/MIT
 */
export const doesNumericFontHaveTnumVariant = (): {
  tnum: boolean;
  fontName: string;
} => {
  // Make a font checker with numerals for which width will vary across fonts.
  // Using numerals because font-width-numeric applies to numeric glyphs and
  // we need to make sure the font we expect will be the one actually used for
  // numeric characters :)
  const container = document.createElement("span");
  container.innerHTML = Array(100).join("15");
  container.style.cssText = containerStyles;
  body.appendChild(container);

  // Get the width of a specific font
  const getWidth = (fontFamily: string): number => {
    container.style.fontFamily = fontFamily;
    return container.clientWidth;
  };

  // Get the widths of monospace, serif & sans-serif, and whether or not the
  // browser supports font-variant-numeric.
  const monoWidth = getWidth("monospace");
  const serifWidth = getWidth("serif");
  const sansWidth = getWidth("sans-serif");

  // Get the fonts and whether we can safely presume them to have tnum variants
  const fonts = [
    { tnum: true, fontName: "-apple-system" },
    { tnum: true, fontName: "system-ui" },
    { tnum: true, fontName: "Segoe UI" },
    { tnum: false, fontName: "Helvetica" },
    { tnum: false, fontName: "Arial" },
    { tnum: false, fontName: "sans-serif" },
    { tnum: true, fontName: "Apple Color Emoji" },
    { tnum: true, fontName: "Segoe UI Emoji" },
    { tnum: true, fontName: "Segoe UI Symbol" },
  ];
  let activeFont = { tnum: false, fontName: "sans-serif" };

  // Iterate over fonts; the first one that's got a unique width is the one the
  // browser will use so further work should assume it's the active one. If
  // nothing happens by the end of this loop we should use an alternate approach
  // to font-variant-numeric because the font itself won't support it.
  for (let i = 0; i < fonts.length; i++) {
    activeFont = fonts[i];
    if (
      monoWidth !== getWidth(`${activeFont.fontName},monospace`) ||
      sansWidth !== getWidth(`${activeFont.fontName},sans-serif`) ||
      serifWidth !== getWidth(`${activeFont.fontName},serif`)
    ) {
      break;
    }
    if (i === fonts.length - 1) {
      activeFont = { tnum: false, fontName: "sans-serif" };
    }
  }

  // Clean up & return
  body.removeChild(container);
  return activeFont;
};

/**
 * Check whether font-variant-numeric is a property the user's browser supports
 */
export const isFontVariantNumericEnabled = (): boolean => {
  let rtn = false;
  const d = document.createElement("div");
  d.style.fontVariantNumeric = "tabular-nums";
  rtn = d.style.fontVariantNumeric === "tabular-nums";
  d.remove();
  return rtn;
};

/**
 * Get the width of the widest expected alpha character a font has
 */
export const getCharWidthForFont = (font: string): string => {
  try {
    const d = document.createElement("span");
    d.innerHTML = "W";
    d.style.cssText = containerStyles;
    d.style.fontFamily = font;
    body.appendChild(d);
    const width = d.offsetWidth;
    const height = d.clientHeight;
    body.removeChild(d);
    return `${(width / height).toString()}em`;
  } catch (e) {
    return "0.7em";
  }
};
